import type { ApiClient } from '@klareospace/pharos-lib-front-utils';
import { toQueryParams, mapPaginatedResponse } from '@klareospace/pharos-lib-front-utils';
import type {
  PaginatedResponse,
  PaginationParams,
  PharosApiResponse,
  PharosPaginatedResponse,
  SortParams,
  Space,
  SpaceWithSiteCount,
  SpaceWithSites,
} from '@klareospace/pharos-lib-types';
import type { SpaceSortableColumn } from '../types/space.types';

// ─── Params ───────────────────────────────────────────────────────────────────

export interface SearchSpacesParams extends PaginationParams {
  ids?: string[];
  name?: string;
  sort?: SortParams<SpaceSortableColumn>;
}

export interface CreateSpaceParams {
  code: string;
  description?: string;
  name: string;
}

export interface UpdateSpaceParams {
  code?: string;
  description?: string;
  name?: string;
}

// ─── API Factory ──────────────────────────────────────────────────────────────

export function createSpaceApi(client: ApiClient): {
  checkCodeExists: (code: string) => Promise<boolean>;
  createSpace: (params: CreateSpaceParams) => Promise<Space>;
  getSpace: (id: string) => Promise<Space>;
  getSpacesWithSites: () => Promise<SpaceWithSites[]>;
  searchSpaces: (params: SearchSpacesParams) => Promise<PaginatedResponse<SpaceWithSiteCount>>;
  updateSpace: (id: string, params: UpdateSpaceParams) => Promise<Space>;
} {
  return {
    checkCodeExists: async (code: string): Promise<boolean> => {
      // Sent as `value`, not `code` — `code` is reserved by Azure Functions (access keys)
      // and never reaches the backend.
      const response = await client.execute<PharosApiResponse<{ exists: boolean }>>(() =>
        client.instance.get('/spaces/check-code', { params: { value: code } }),
      );
      return response.data?.exists ?? false;
    },

    createSpace: async (params: CreateSpaceParams): Promise<Space> => {
      const response = await client.execute<PharosApiResponse<Space>>(() => client.instance.post('/spaces', params));
      if (!response.data) {
        throw new Error('createSpace: unexpected empty response');
      }
      return response.data;
    },

    getSpace: async (id: string): Promise<Space> => {
      const response = await client.execute<PharosApiResponse<Space>>(() => client.instance.get(`/spaces/${id}`));
      if (!response.data) {
        throw new Error('getSpace: unexpected empty response');
      }
      return response.data;
    },

    getSpacesWithSites: async (): Promise<SpaceWithSites[]> => {
      const response = await client.execute<PharosApiResponse<SpaceWithSites[]>>(() =>
        client.instance.get('/spaces/with-sites'),
      );
      if (!response.data) {
        throw new Error('getSpacesWithSites: unexpected empty response');
      }
      return response.data;
    },

    searchSpaces: async (params: SearchSpacesParams): Promise<PaginatedResponse<SpaceWithSiteCount>> => {
      const body = toSearchBody(params);
      const query = toQueryParams(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<SpaceWithSiteCount>>>(() =>
        client.instance.post('/spaces/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('searchSpaces: unexpected empty response');
      }
      return mapPaginatedResponse(response.data);
    },

    updateSpace: async (id: string, params: UpdateSpaceParams): Promise<Space> => {
      const response = await client.execute<PharosApiResponse<Space>>(() =>
        client.instance.patch(`/spaces/${id}`, params),
      );
      if (!response.data) {
        throw new Error('updateSpace: unexpected empty response');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toSearchBody(params: SearchSpacesParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedName = params.name?.trim();

  const conditionalFields: [string, unknown][] = [
    ['name', trimmedName],
    ['ids', params.ids],
  ];

  for (const [key, value] of conditionalFields) {
    if (Array.isArray(value) ? value.length > 0 : value) {
      body[key] = value;
    }
  }

  return body;
}
